import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { RankingType } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import TrendIcon from '@flipster/react/icon/generated/TrendIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import { competitionApiBunja, competitionTabsBunja, myCompetitionInfoBunja } from '../../state';

export const NoticeBanner = () => {
  const { myCompetitionInfoDataAtom } = useBunja(myCompetitionInfoBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const myCompetitionInfoData = useAtomValue(myCompetitionInfoDataAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const competitionStatus = useAtomValue(competitionStatusAtom);

  const isRoi = selectedEventTabValue === 'roi';

  const myCompetitionInfo = myCompetitionInfoData?.[selectedEventTabValue as RankingType];

  if (!myCompetitionInfo || competitionStatus === 'FINISHED') return null;

  const getMessage = () => {
    const { valueToNextPrizeGroup, nextPrizeRank, rank } = myCompetitionInfo;
    const amountSymbol = isRoi ? (
      <Formatted.Percent value={valueToNextPrizeGroup} isRate toFixed />
    ) : (
      <Formatted.ShortNumber value={valueToNextPrizeGroup} suffix={UsdtCurrency} />
    );

    if (!rank)
      return selectedEventTabValue === 'volume' ? (
        <FormattedMessage
          id='Competition.r251215.Trade_more_for_top_rank'
          values={{
            amountSymbol,
            rank: <Formatted.Number value={nextPrizeRank} />,
          }}
        />
      ) : isRoi ? (
        <FormattedMessage
          id='Tmp.inseok.Increase_roi_more_for_top_rank'
          values={{
            amountSymbol,
            rank: <Formatted.Number value={nextPrizeRank} />,
          }}
        />
      ) : (
        <FormattedMessage
          id='Competition.r251217.Earn_more_for_top_rank'
          values={{
            amountSymbol,
            rank: <Formatted.Number value={nextPrizeRank} />,
          }}
        />
      );
    if (rank === 1)
      return <FormattedMessage id='Competition.r251215.Keep_trading_secure_lead' values={{ rank: '#1' }} />;
    return selectedEventTabValue === 'volume' ? (
      <FormattedMessage
        id='Competition.r251215.Trade_more_to_top_rank'
        values={{
          amountSymbol,
          rank: <Formatted.Number value={nextPrizeRank} />,
        }}
      />
    ) : isRoi ? (
      <FormattedMessage
        id='Tmp.inseok.Increase_roi_more_to_top_rank'
        values={{
          amountSymbol,
          rank: <Formatted.Number value={nextPrizeRank} />,
        }}
      />
    ) : (
      <FormattedMessage
        id='Competition.r251217.Earn_more_to_top_rank'
        values={{
          amountSymbol,
          rank: <Formatted.Number value={nextPrizeRank} />,
        }}
      />
    );
  };

  if (!myCompetitionInfo.nextPrizeRank || !myCompetitionInfo.valueToNextPrizeGroup) return null;

  return (
    <section
      className={hstack({
        py: 16,
        px: 24,
        rounded: { base: 12, tablet: 16 },
        bg: 'background.default.level-1',
        gap: 10,
      })}
    >
      <TrendIcon size={24} color={token('colors.content.accent.new')} />
      <p
        className={css({
          flex: 1,
          textStyle: 'Text/M/Regular',
          color: 'content.default.level-5',
          whiteSpace: 'pre-line',
          textAlign: 'left',
        })}
      >
        {getMessage()}
      </p>
    </section>
  );
};
